import React, { useState } from "react";
import "./ReferralCardCss.css";

interface AccordionCardProps {
  title: string;
  icon?: JSX.Element;
  buttonText?: string;
  onButtonClick?: () => void;
  children?: React.ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean; // Optional prop to control initial state
}

export default function AccordionReferralCard({
  title,
  icon,
  buttonText,
  onButtonClick,
  disabled,
  children,
  defaultOpen = false, // Default to closed
}: AccordionCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="card-container">
      {/* Always visible header */}
      <div className="card-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            {/* Accordion toggle button */}
            <button
              onClick={toggleAccordion}
              className="mr-2 p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transition-transform duration-200 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#8D8D8D"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Default icon or custom icon */}
            {!icon && (
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2"
              >
                <path
                  d="M3.38984 3.92969C3.01191 3.92969 2.64946 4.07982 2.38222 4.34706C2.11498 4.6143 1.96484 4.97675 1.96484 5.35469V7.25469C1.96484 7.63262 2.11498 7.99508 2.38222 8.26231C2.64946 8.52955 3.01191 8.67969 3.38984 8.67969H5.28984C5.66778 8.67969 6.03023 8.52955 6.29747 8.26231C6.56471 7.99508 6.71484 7.63262 6.71484 7.25469V5.35469C6.71484 4.97675 6.56471 4.6143 6.29747 4.34706C6.03023 4.07982 5.66778 3.92969 5.28984 3.92969H3.38984ZM9.08984 4.87969C8.96387 4.87969 8.84305 4.92973 8.75397 5.01881C8.66489 5.10789 8.61484 5.22871 8.61484 5.35469C8.61484 5.48067 8.66489 5.60148 8.75397 5.69056C8.84305 5.77964 8.96387 5.82969 9.08984 5.82969H16.6898C16.8158 5.82969 16.9366 5.77964 17.0257 5.69056C17.1148 5.60148 17.1648 5.48067 17.1648 5.35469C17.1648 5.22871 17.1148 5.10789 17.0257 5.01881C16.9366 4.92973 16.8158 4.87969 16.6898 4.87969H9.08984ZM9.08984 6.77969C8.96387 6.77969 8.84305 6.82973 8.75397 6.91881C8.66489 7.00789 8.61484 7.12871 8.61484 7.25469C8.61484 7.38067 8.66489 7.50148 8.75397 7.59056C8.84305 7.67964 8.96387 7.72969 9.08984 7.72969H14.7898C14.9158 7.72969 15.0366 7.67964 15.1257 7.59056C15.2148 7.50148 15.2648 7.38067 15.2648 7.25469C15.2648 7.12871 15.2148 7.00789 15.1257 6.91881C15.0366 6.82973 14.9158 6.77969 14.7898 6.77969H9.08984ZM3.38984 10.5797C3.01191 10.5797 2.64946 10.7298 2.38222 10.9971C2.11498 11.2643 1.96484 11.6268 1.96484 12.0047V13.9047C1.96484 14.2826 2.11498 14.6451 2.38222 14.9123C2.64946 15.1796 3.01191 15.3297 3.38984 15.3297H5.28984C5.66778 15.3297 6.03023 15.1796 6.29747 14.9123C6.56471 14.6451 6.71484 14.2826 6.71484 13.9047V12.0047C6.71484 11.6268 6.56471 11.2643 6.29747 10.9971C6.03023 10.7298 5.66778 10.5797 5.28984 10.5797H3.38984ZM9.08984 11.5297C8.96387 11.5297 8.84305 11.5797 8.75397 11.6688C8.66489 11.7579 8.61484 11.8787 8.61484 12.0047C8.61484 12.1307 8.66489 12.2515 8.75397 12.3406C8.84305 12.4296 8.96387 12.4797 9.08984 12.4797H16.6898C16.8158 12.4797 16.9366 12.4296 17.0257 12.3406C17.1148 12.2515 17.1648 12.1307 17.1648 12.0047C17.1648 11.8787 17.1148 11.7579 17.0257 11.6688C16.9366 11.5797 16.8158 11.5297 16.6898 11.5297H9.08984ZM9.08984 13.4297C8.96387 13.4297 8.84305 13.4797 8.75397 13.5688C8.66489 13.6579 8.61484 13.7787 8.61484 13.9047C8.61484 14.0307 8.66489 14.1515 8.75397 14.2406C8.84305 14.3296 8.96387 14.3797 9.08984 14.3797H14.7898C14.9158 14.3797 15.0366 14.3296 15.1257 14.2406C15.2148 14.1515 15.2648 14.0307 15.2648 13.9047C15.2648 13.7787 15.2148 13.6579 15.1257 13.5688C15.0366 13.4797 14.9158 13.4297 14.7898 13.4297H9.08984Z"
                  fill="#8D8D8D"
                />
              </svg>
            )}
            {icon}

            <p className="card-container-title">{title}</p>
          </div>

          {/* Add button */}
          {buttonText && onButtonClick && (
            <button
              className="card-container-button"
              onClick={onButtonClick}
              disabled={disabled}
              style={disabled ? { cursor: "not-allowed" } : { cursor: "pointer" }}
            >
              <svg
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0846 8.08073H7.58464V11.5807H6.41797V8.08073H2.91797V6.91406H6.41797V3.41406H7.58464V6.91406H11.0846V8.08073Z"
                  fill="#212121"
                />
              </svg>
              {buttonText}
            </button>
          )}
        </div>
      </div>

      {/* Collapsible content */}
      <div
        className={`accordion-content ${
          isOpen ? "accordion-content-open" : "accordion-content-closed"
        }`}
      >
        {children}
      </div>
    </div>
  );
}